import { Component, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonIconComponent, ChartDataSeries, ChartLineComponent, TileComponent } from '@tecalisdev/infinity';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize } from 'rxjs/internal/operators/finalize';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { formatFilterTable } from '../../../core/utils/utils';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { IGraphProcessBody } from '../../models/panel.models';
import { PanelCurlService } from '../../services/panel-curl.service';
import { PanelHelperService } from '../../services/panel-helper.service';
import { PanelService } from '../../services/panel.service';

@Component({
	selector: 'app-process-chart',
	imports: [TranslatePipe, ChartLineComponent, TileComponent, ButtonIconComponent, ModalCurlRequestComponent],
	templateUrl: './process-chart.component.html',
	styleUrl: './process-chart.component.scss',
})
export class ProcessChartComponent implements OnInit {
	private panelService = inject(PanelService);
	private panelHelperService = inject(PanelHelperService);
	protected rolePermissions = inject(RolePermissionsService);
	private panelCurlService = inject(PanelCurlService);
	private translateService = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	private request?: Subscription;

	public dataset: ChartDataSeries[] = [];
	public labels: string[] = [];
	public readonly loading = signal(false);
	public readonly curlModalOpen = signal(false);
	private lastResponse: IGraphProcessBody | null = null;

	constructor() {
		effect(() => {
			const rawFilters = this.panelHelperService.currentFilters();
			if (rawFilters) {
				const filters = { ...formatFilterTable(rawFilters) };
				this.loadChart(filters);
			}
		});
	}

	ngOnInit(): void {
		this.translateService.onLangChange
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.lastResponse) {
					this.buildDataset(this.lastResponse);
				}
			});
	}

	public openCurlModal(): void {
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public getGraphProcessCurl(): string {
		const rawFilters = this.panelHelperService.currentFilters();
		if (!rawFilters) return '';
		const filters = { ...formatFilterTable({ ...rawFilters }) };
		return this.panelCurlService.buildGraphProcessCurl(filters);
	}

	private loadChart(filters: any): void {
		this.request?.unsubscribe();
		this.loading.set(true);
		this.request = this.panelService
			.getGraphProcess(filters)
			.pipe(finalize(() => this.loading.set(false)))
			.subscribe({
				next: (response: any) => {
					const data = response?.data;
					if (data?.totals) {
						this.lastResponse = data;
						this.buildDataset(data);
					}
				},
				error: () => {
					this.lastResponse = null;
					this.dataset = [];
					this.labels = [];
				},
			});
	}

	private buildDataset(response: IGraphProcessBody): void {
		const dates = Object.keys(response.totals.OK || {}).sort();

		this.labels = dates.map((date) => {
			const [year, month, day] = date.split('-');
			if (day === undefined) return `${month}/${year}`;
			return `${day}/${month}`;
		});

		this.dataset = [
			{
				name: this.translateService.instant('PANEL.STATS.APPROVED'),
				data: dates.map((date) => response.totals.OK[date] || 0),
			},
			{
				name: this.translateService.instant('PANEL.STATS.PENDING'),
				data: dates.map((date) => response.totals.PENDING[date] || 0),
			},
			{
				name: this.translateService.instant('PANEL.STATS.REJECTED'),
				data: dates.map((date) => response.totals.KO[date] || 0),
			},
			{
				name: this.translateService.instant('PANEL.STATS.ABANDONED'),
				data: dates.map((date) => response.totals.CANCELLED[date] || 0),
			},
		];
	}
}
